import { useMemo } from "react";
import { useApi, QueryKeys, configureApi } from "@akina/infra";
import type { AppLocale } from "@akina/infra";
import { useQuery } from "@tanstack/react-query";
import { sharedEnvironment } from "../../config";
import { useLocaleStore } from "./useLocaleStore";

const STALE_TIME = 1000 * 60; // Consider data fresh for 1 minute
const REFRESH_INTERVAL = 1000 * 60 * 5; // Force a refresh of the data every 5 minutes
const GC_TIME = Infinity; // Keep data in memory until the app is closed

export const useFetchLocaleCopy = (slugs: string[], localeOverride?: AppLocale) => {
  const { api } = useApi();
  const { locale } = useLocaleStore();

  const effectiveLocale = localeOverride ?? locale;

  const overrideApi = useMemo(() => {
    if (!localeOverride) return null;
    return configureApi({
      baseUrl: sharedEnvironment.apiBaseUrl,
      getAccessToken: undefined,
      locale: localeOverride,
      cmsLinkWithEnv: sharedEnvironment.cmsLinkWithEnv,
    });
  }, [localeOverride]);

  return useQuery({
    // Sort the slugs array to avoid false re-renders
    queryKey: [
      QueryKeys.fetchLocaleCopy,
      effectiveLocale,
      !!localeOverride,
      JSON.stringify([...slugs].sort((a, b) => a.localeCompare(b))),
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const activeApi = overrideApi ?? api;
      return activeApi.localeCopy.getCmsCopy(slugs, signal);
    },

    staleTime: STALE_TIME,
    refetchInterval: REFRESH_INTERVAL,
    refetchIntervalInBackground: true,
    gcTime: GC_TIME,
    refetchOnWindowFocus: true,
    enabled: !!effectiveLocale && slugs.length > 0,
  });
};
